/**
 * Extra (brand-optional) sidebar topics, appended after Claims/Contact when the
 * brand's helpConfig defines both a label and a route for the key. Add new
 * standalone HelpCenter pages here instead of hand-rolling another `if` block.
 */
const EXTRA_TOPIC_KEYS = ['sizeGuide', 'trykBroderi'];

/**
 * Shared builder for HelpCenter sidebar topics (FAQ / Claims / Contact / extras).
 *
 * @param {Object} cfg
 * @param {string} cfg.context                      - Page calling the builder ('faq'|'claims'|'contact'|one of EXTRA_TOPIC_KEYS)
 * @param {Array}  cfg.categories                   - FAQ categories
 * @param {Object} cfg.labels                       - { popular, claims?, contact?, ...extras }
 * @param {Object} cfg.routes                       - { faq, claims?, contact?, ...extras }
 * @param {Function} cfg.navigate                   - react-router navigate
 * @param {Function} [cfg.setActiveCategory]        - FAQ-only
 * @param {Function} [cfg.setQuery]                 - FAQ-only
 *
 * @returns {Array<{id,label,onClick}>}
 */
export function buildHelpTopics({
  context,
  categories,
  labels,
  routes,
  navigate,
  setActiveCategory,
  setQuery,
}) {
  const goFAQ = (categoryId) => () => {
    if (context === 'faq') {
      setActiveCategory?.(categoryId ?? null);
      setQuery?.('');
      // In-page category switch keeps the same route, so ScrollToTop
      // (which watches pathname) won't fire — reset scroll manually.
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      navigate(categoryId ? `${routes.faq}?category=${categoryId}` : routes.faq);
    }
  };

  const topics = [
    { id: 'popular', label: labels.popular, onClick: goFAQ(null) },
    ...categories.map((cat) => ({
      id: `cat:${cat.id}`,
      label: cat.label,
      onClick: goFAQ(cat.id),
    })),
    /* Claims and Contact are brand-optional: omit label/route in helpConfig to
       hide one (OVER/LL has no contact page — it appends a mailto topic itself). */
    ...(labels.claims && routes.claims
      ? [{ id: 'claims', label: labels.claims, onClick: () => navigate(routes.claims) }]
      : []),
    ...(labels.contact && routes.contact
      ? [{ id: 'contact', label: labels.contact, onClick: () => navigate(routes.contact) }]
      : []),
  ];

  EXTRA_TOPIC_KEYS.forEach((key) => {
    if (labels[key] && routes[key]) {
      topics.push({
        id: key,
        label: labels[key],
        onClick: () => navigate(routes[key]),
      });
    }
  });

  return topics;
}

/**
 * Resolve the active topic id from page state.
 */
export function getActiveTopicId({ context, activeCategory, isSearching }) {
  if (context === 'faq') {
    if (isSearching) return null;
    return activeCategory ? `cat:${activeCategory}` : 'popular';
  }
  return context; // 'claims' | 'contact'
}
